import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import TaskDetailModal from '@/components/TaskDetailModal';
import QuickUpdateModal from '@/components/QuickUpdateModal';
import AITaskPreview from '@/components/AITaskPreview';
import WorkflowTemplatePickerModal from '@/components/WorkflowTemplatePickerModal';
import { SubtaskProgress } from '@/components/TaskDetailModal';
import {
  Plus,
  CheckCircle,
  Circle,
  Trash,
  Sparkle,
  FunnelSimple,
  X,
  PencilSimple,
  ListChecks,
  Lightning
} from '@phosphor-icons/react';
import { format, parseISO, isValid } from 'date-fns';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function Tasks({ user }) {
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [creatingFromNL, setCreatingFromNL] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [filterStatus, setFilterStatus] = useState(location.state?.filterStatus || 'all');
  const [showManualForm, setShowManualForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [quickUpdateTask, setQuickUpdateTask] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showProjectSuggestions, setShowProjectSuggestions] = useState(false);
  const [showWorkflowPicker, setShowWorkflowPicker] = useState(false);
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    project_client: '',
    priority: 'medium',
    due_date: '',
    tags: ''
  });

  useEffect(() => {
    fetchTasks();
    axios.get(`${API}/tasks/projects`, { withCredentials: true })
      .then(res => setProjects(res.data)).catch(() => {});
  }, [filterStatus]);

  const fetchTasks = async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await axios.get(`${API}/tasks`, { params, withCredentials: true });
      setTasks(response.data.items || response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTaskFromNaturalLanguage = async () => {
    if (!naturalLanguageInput.trim()) return;
    setCreatingFromNL(true);
    try {
      const response = await axios.post(
        `${API}/ai/parse-task`,
        { text: naturalLanguageInput, context: 'new_task' },
        { withCredentials: true }
      );
      setAiPreview(response.data);
    } catch (error) {
      console.error('Failed to parse task:', error);
      const status = error.response?.status;
      if (status === 429) {
        alert('AI service is temporarily busy. Please wait a moment and try again, or use the manual form.');
      } else {
        alert('AI parsing failed. Please try the manual form.');
      }
    } finally {
      setCreatingFromNL(false);
    }
  };

  const handleAiPreviewSave = (newTask) => {
    setTasks([newTask, ...tasks]);
    setAiPreview(null);
    setNaturalLanguageInput('');
    setShowCreateModal(false);
  };

  const handleWorkflowTaskCreated = (newTask) => {
    setTasks([newTask, ...tasks]);
    setShowWorkflowPicker(false);
    setShowCreateModal(false);
  };

  const createManualTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...newTask,
        tags: newTask.tags.split(',').map(t => t.trim()).filter(t => t),
        project_client: newTask.project_client.trim() || undefined
      };
      const response = await axios.post(`${API}/tasks`, taskData, { withCredentials: true });
      setTasks([response.data, ...tasks]);
      setNewTask({ title: '', description: '', project_client: '', priority: 'medium', due_date: '', tags: '' });
      setShowManualForm(false);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create task:', error);
      alert('Failed to create task. Please try again.');
    }
  };

  const toggleTaskStatus = async (task) => {
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      const response = await axios.put(`${API}/tasks/${task.task_id}`, { status: newStatus }, { withCredentials: true });
      setTasks(prev => prev.map(t => t.task_id === task.task_id ? response.data : t));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  // Sort: pending/active first, completed last (only matters in "all" view)
  const sortedTasks = filterStatus === 'all'
    ? [...tasks].sort((a, b) => {
        const aCompleted = a.status === 'completed' ? 1 : 0;
        const bCompleted = b.status === 'completed' ? 1 : 0;
        return aCompleted - bCompleted;
      })
    : tasks;

  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      setTasks(tasks.filter(t => t.task_id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM d, yyyy') : null;
    } catch { return null; }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-50 text-red-600 border-red-200';
      case 'medium': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'low': return 'bg-slate-50 text-slate-600 border-slate-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      
      <main className="max-w-5xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-2">Tasks</h1>
            <p className="text-sm md:text-base text-slate-500">Organize and track your tasks</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-navy text-white hover:bg-navy-hover transition-all duration-200 shadow-sm hover:shadow-md font-medium px-4 py-2 rounded-lg flex items-center gap-2"
            data-testid="create-task-modal-btn"
          >
            <Plus size={20} weight="bold" />
            <span className="hidden sm:inline">New Task</span>
          </button>
        </div>

        {/* Filter */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <FunnelSimple size={18} className="text-slate-400" />
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  filterStatus === status
                    ? 'bg-navy text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Tasks List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-navy border-t-transparent"></div>
          </div>
        ) : sortedTasks.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="no-tasks">
            <p className="text-slate-400">No tasks found. Create your first task!</p>
          </div>
        ) : (
          <LayoutGroup>
          <div className="space-y-3">
            {sortedTasks.map((task) => (
              <motion.div
                key={task.task_id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ layout: { duration: 0.3, ease: 'easeInOut' } }}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200 cursor-pointer"
                data-testid={`task-${task.task_id}`}
                onClick={() => setSelectedTask(task)}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleTaskStatus(task); }}
                    className="flex-shrink-0 mt-1"
                    data-testid={`toggle-task-${task.task_id}`}
                  >
                    {task.status === 'completed' ? (
                      <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                    ) : (
                      <Circle size={24} weight="regular" className="text-slate-300 hover:text-navy transition-colors" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold text-slate-900 mb-1 ${task.status === 'completed' ? 'line-through text-slate-400' : ''}`}>
                      {task.title}
                    </h3>
                    
                    {task.description && <p className="text-sm text-slate-500 mb-2">{task.description}</p>}

                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-md px-2.5 py-0.5 text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                      
                      {task.project_client && (
                        <span className="rounded-md px-2.5 py-0.5 text-xs font-medium bg-blue-50 text-navy border border-blue-200">
                          {task.project_client}
                        </span>
                      )}

                      {task.due_date && <span className="text-xs text-slate-400">Due: {formatDate(task.due_date)}</span>}

                      {task.tags?.map((tag, idx) => (
                        <span key={idx} className="rounded-md px-2.5 py-0.5 text-xs font-medium border bg-slate-50 text-slate-600 border-slate-200">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {task.subtasks && task.subtasks.length > 0 && (
                      <div className="mt-2 flex items-center gap-2" data-testid={`subtask-compact-${task.task_id}`}>
                        <ListChecks size={13} className="text-slate-400" />
                        <div className="flex-1 max-w-[120px] h-1 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${task.subtasks.length > 0 ? Math.round(task.subtasks.filter(s => s.status === 'completed' || s.completed).length / task.subtasks.length * 100) : 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-400">
                          {task.subtasks.filter(s => s.status === 'completed' || s.completed).length}/{task.subtasks.length}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setQuickUpdateTask(task); }}
                      className="text-xs font-medium text-navy bg-blue-50 hover:bg-blue-100 transition-colors px-2.5 py-1.5 rounded-md flex items-center gap-1"
                      data-testid={`quick-update-btn-${task.task_id}`}
                    >
                      <PencilSimple size={12} weight="bold" /> Update
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteTask(task.task_id); }}
                      className="text-slate-300 hover:text-red-600 transition-colors"
                      data-testid={`delete-task-${task.task_id}`}
                    >
                      <Trash size={20} weight="regular" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          </LayoutGroup>
        )}

        {/* Create Task Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
                <h2 className="text-xl font-heading font-medium tracking-tight text-slate-900">Create New Task</h2>
                <button
                  onClick={() => { setShowCreateModal(false); setShowManualForm(false); setNaturalLanguageInput(''); }}
                  className="text-slate-400 hover:text-slate-600"
                  data-testid="close-modal-btn"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6">
                {!showManualForm ? (
                  <div>
                    {aiPreview ? (
                      <AITaskPreview
                        parsed={aiPreview.parsed}
                        originalText={aiPreview.original_text}
                        suggestedTemplates={aiPreview.suggested_templates}
                        mode="new_task"
                        projects={projects}
                        onSave={handleAiPreviewSave}
                        onCancel={() => setAiPreview(null)}
                        onUseTemplate={(templateId) => {
                          setAiPreview(null);
                          setNaturalLanguageInput('');
                          axios.post(`${API}/workflow-templates/${templateId}/apply`, {}, { withCredentials: true })
                            .then(res => { handleWorkflowTaskCreated(res.data); })
                            .catch(() => alert('Failed to apply template.'));
                        }}
                      />
                    ) : (
                      <>
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <Sparkle size={16} weight="duotone" className="text-navy" />
                            Describe your task in plain language
                          </label>
                          <textarea
                            value={naturalLanguageInput}
                            onChange={(e) => setNaturalLanguageInput(e.target.value)}
                            placeholder='e.g., "FIRS VAT filing for March — Lafarge Africa" or "Review employment contract for Adaeze Okafor"'
                            className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-3 w-full text-slate-900 min-h-[100px] resize-none"
                            data-testid="natural-language-input"
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); createTaskFromNaturalLanguage(); } }}
                          />
                          <p className="text-xs text-slate-400 mt-2">
                            AI will extract title, due date, project/client, priority, and notes.
                          </p>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={createTaskFromNaturalLanguage}
                            disabled={creatingFromNL || !naturalLanguageInput.trim()}
                            className="flex-1 bg-navy text-white hover:bg-navy-hover transition-all duration-200 shadow-sm font-medium px-4 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            data-testid="create-from-nl-btn"
                          >
                            <Sparkle size={16} weight="fill" />
                            {creatingFromNL ? 'Parsing...' : 'Parse with AI'}
                          </button>
                          <button
                            onClick={() => setShowManualForm(true)}
                            className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-3 rounded-lg"
                            data-testid="manual-form-btn"
                          >
                            Manual Entry
                          </button>
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <button
                            onClick={() => setShowWorkflowPicker(true)}
                            className="w-full bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors font-medium px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-slate-600 text-sm"
                            data-testid="start-from-template-btn"
                          >
                            <Lightning size={16} weight="duotone" className="text-navy" />
                            Start from workflow template
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <form onSubmit={createManualTask} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                      <input
                        type="text" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        required className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                        data-testid="manual-title-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                      <textarea
                        value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                        className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900 min-h-[80px]"
                        data-testid="manual-description-input"
                      />
                    </div>

                    <div className="relative">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Project / Client</label>
                      <input
                        type="text" value={newTask.project_client}
                        onChange={(e) => { setNewTask({ ...newTask, project_client: e.target.value }); setShowProjectSuggestions(true); }}
                        onFocus={() => setShowProjectSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowProjectSuggestions(false), 150)}
                        placeholder="e.g., Dangote Group, MTN Nigeria"
                        className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                        data-testid="manual-project-input"
                      />
                      {showProjectSuggestions && projects.filter(p => p.toLowerCase().includes(newTask.project_client.toLowerCase()) && p !== newTask.project_client).length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md z-10 max-h-32 overflow-y-auto">
                          {projects.filter(p => p.toLowerCase().includes(newTask.project_client.toLowerCase()) && p !== newTask.project_client).map(p => (
                            <button key={p} type="button" className="block w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                              onMouseDown={() => { setNewTask({ ...newTask, project_client: p }); setShowProjectSuggestions(false); }}
                            >{p}</button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                        <select value={newTask.priority} onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                          className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                          data-testid="manual-priority-input">
                          <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Due Date</label>
                        <input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                          className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                          data-testid="manual-date-input" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Tags (comma-separated)</label>
                      <input type="text" value={newTask.tags} onChange={(e) => setNewTask({ ...newTask, tags: e.target.value })}
                        placeholder="compliance, tax, litigation"
                        className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                        data-testid="manual-tags-input" />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button type="submit" className="flex-1 bg-navy text-white hover:bg-navy-hover transition-all duration-200 shadow-sm font-medium px-4 py-3 rounded-lg"
                        data-testid="submit-manual-task-btn">Create Task</button>
                      <button type="button" onClick={() => setShowManualForm(false)}
                        className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-3 rounded-lg"
                        data-testid="back-to-nl-btn">Back</button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {selectedTask && (
          <TaskDetailModal task={selectedTask} onClose={() => setSelectedTask(null)} onUpdate={fetchTasks}
            onDelete={(taskId) => { setTasks(tasks.filter(t => t.task_id !== taskId)); setSelectedTask(null); }} user={user} />
        )}

        {quickUpdateTask && (
          <QuickUpdateModal taskId={quickUpdateTask.task_id} taskTitle={quickUpdateTask.title}
            currentStatus={quickUpdateTask.status} currentProject={quickUpdateTask.project_client}
            onClose={() => setQuickUpdateTask(null)} onComplete={() => { setQuickUpdateTask(null); fetchTasks(); }} />
        )}

        {showWorkflowPicker && (
          <WorkflowTemplatePickerModal onClose={() => setShowWorkflowPicker(false)} onTaskCreated={handleWorkflowTaskCreated} />
        )}
      </main>
    </div>
  );
}

export default Tasks;
